import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { CheckboxOptions } from './CheckboxFacet';
import type { CarFilters } from '../../CarRentalConsole.types';
import { FilterFacet } from '@apps/stays/components/filters/FilterFacet';
import { StyledCheckboxV2 } from '@apps/stays/components/filters/StyledCheckboxV2';

type SpecificationsFacetProps = Readonly<{
  filters: CarFilters;
  supportedFuelTypes: ReadonlyArray<string>;
  supportedDriveTypes: ReadonlyArray<string>;
  supportedTransmissions: ReadonlyArray<string>;
  formatSpecification: (value: string) => string;
  onFiltersChange: (filters: CarFilters) => void;
  fuelTypeCounts: Readonly<Record<string, number>>;
  driveTypeCounts: Readonly<Record<string, number>>;
  transmissionCounts: Readonly<Record<string, number>>;
}>;

const toggleValue = <T,>(source: ReadonlyArray<T>, value: T): ReadonlyArray<T> => (source.includes(value) ? source.filter(item => item !== value) : [...source, value]);

export const SpecificationsFacet = ({
  filters,
  fuelTypeCounts,
  driveTypeCounts,
  onFiltersChange,
  transmissionCounts,
  supportedFuelTypes,
  supportedDriveTypes,
  formatSpecification,
  supportedTransmissions,
}: SpecificationsFacetProps) => {
  const { tq } = useTranslation('carRentalConsole');

  return (
    <FilterFacet title={tq`Specifications`}>
      <Flex
        gap={5}
        flexDirection="column"
      >
        <StyledCheckboxV2
          id="car-facet-air-conditioned"
          isChecked={filters.airConditionedOnly}
          onChange={() => onFiltersChange({ ...filters, airConditionedOnly: !filters.airConditionedOnly })}
        >
          <TextV2
            color="mono14"
            typographyVariant="body1"
          >
            {tq`Air conditioning`}
          </TextV2>
        </StyledCheckboxV2>
        <CheckboxOptions
          counts={transmissionCounts}
          options={supportedTransmissions}
          formatLabel={formatSpecification}
          selected={filters.transmissionTypes}
          getId={value => `car-facet-transmission:${value}`}
          onToggle={value => onFiltersChange({ ...filters, transmissionTypes: toggleValue(filters.transmissionTypes, value) })}
        />
        <CheckboxOptions
          counts={fuelTypeCounts}
          selected={filters.fuelTypes}
          options={supportedFuelTypes}
          formatLabel={formatSpecification}
          getId={value => `car-facet-fuel:${value}`}
          onToggle={value => onFiltersChange({ ...filters, fuelTypes: toggleValue(filters.fuelTypes, value) })}
        />
        <CheckboxOptions
          counts={driveTypeCounts}
          selected={filters.driveTypes}
          options={supportedDriveTypes}
          formatLabel={formatSpecification}
          getId={value => `car-facet-drive:${value}`}
          onToggle={value => onFiltersChange({ ...filters, driveTypes: toggleValue(filters.driveTypes, value) })}
        />
      </Flex>
    </FilterFacet>
  );
};
